/**
 * Opportunities API client — Story 7.4 (Epic 7 Area B).
 *
 * Thin fetch helpers for `apps/api/src/routes/opportunities.ts`. Cookies
 * included (better-auth session); non-2xx throws so TanStack Query routes it
 * through the standard error pipeline.
 *
 * `applicationCount` is `null` until the `applications` table lands (Area C /
 * Story 7.6) — the UI renders "—".
 */
import { API_BASE } from '@/lib/api'
import { readProblemMessage } from '@/lib/api/problem'
// O contrato de partilha é o MESMO das outras duas (avaliação e candidatura) —
// importado, não duplicado. É o que permite ao cartão da UI servir os três.
import type { ShareState } from '@/lib/api/evaluations'
import type { ShareEmailRecipients } from '@caliber/shared/dtos'

export type OpportunityStatus = 'open' | 'closed' | 'archived'

export interface Opportunity {
  id: string
  slug: string
  title: string
  jobDescription: string | null
  status: OpportunityStatus
  interviewAssessmentId: string | null
  practicalAssessmentId: string | null
  /**
   * Vínculos da migration 0022 — o cliente (empresa) e o perfil (molde do
   * cargo). Anuláveis: uma vaga pode existir antes de qualquer um dos dois.
   */
  customerId: string | null
  jobProfileId: string | null
  /**
   * Nomes resolvidos por LEFT JOIN no servidor, para a lista não fazer uma
   * chamada por linha só para escrever "B3".
   *
   * Presentes na LISTA e no DETALHE. Os caminhos de ESCRITA (POST/PATCH) não os
   * calculam — a resposta de um save traz os ids, não os nomes. Por isso nada
   * deve renderizar estes campos a partir do retorno de uma mutação; a leitura
   * seguinte (invalidate → refetch) é que os traz.
   */
  customerName: string | null
  jobProfileName: string | null
  /**
   * O processo seletivo desta vaga (migration 0024, onda W2). `null` só é
   * possível se alguém tiver editado o D1 à mão — o backfill preencheu o
   * histórico e o POST resolve o padrão quando ninguém escolhe.
   */
  pipelineId: string | null
  /** Resolvido por LEFT JOIN, como `customerName` — mesmas ressalvas acima. */
  pipelineName: string | null
  createdBy: string | null
  /** ISO-8601 strings (Hono `c.json` serialises the Date columns). */
  createdAt: string
  updatedAt: string
  /** Deferred to Story 7.6 (applications table) — `null` = not yet available. */
  applicationCount: number | null
  /** Imported job-description document (Story 12.1) — `null` = none attached. */
  documentKey: string | null
  documentName: string | null
  documentMime: string | null
  documentSize: number | null
  /**
   * Quantas candidaturas desta vaga ainda percorrem o processo (W2).
   *
   * Presente em DOIS sítios, e ausente na listagem — opcional por isso, e não
   * porque possa faltar onde é prometido:
   *   • `GET /:id` — é o número que decide se o select do processo fica
   *     bloqueado, e é o que a frase de explicação cita;
   *   • a resposta do `archiveOpportunity` — é o que a interface usa para dizer,
   *     no momento do gesto, quantas candidaturas o arquivamento NÃO encerra.
   */
  activeApplicationCount?: number
  /**
   * Quantos perfis o CATÁLOGO PÚBLICO desta vaga mostraria agora (Story 16.1).
   *
   * ⚠️ NÃO é a contagem de candidaturas, e a diferença importa: uma vaga pode
   * ter 30 candidaturas activas e zero na vitrine (nada apresentado, ou perfis
   * ainda retidos pela trava). O servidor calcula-o com o MESMO predicado da
   * rota anónima, para o número não poder divergir do que o link mostra.
   *
   * Só no DETALHE — `undefined` significa "o servidor não disse", e nunca zero.
   */
  presentedCount?: number
  /**
   * Story 23.8 — o link público da vaga: ligado ou não, e o token. Vêm na lista E
   * no detalhe. O token sozinho NÃO diz que o link funciona: desligar preserva-o
   * na linha (histórico), e só `publicShareEnabled` diz se ele abre.
   */
  publicShareEnabled?: boolean | null
  publicShareToken?: string | null
}

/** Metadata + presigned download URL for an opportunity's document (Story 12.1). */
export interface OpportunityDocument {
  url: string
  name: string | null
  mime: string | null
  size: number | null
}

export interface OpportunitiesListResponse {
  items: Opportunity[]
  page: number
  pageSize: number
  total: number
}

export interface CreateOpportunityInput {
  title: string
  slug?: string
  jobDescription?: string | null
  status?: OpportunityStatus
  interviewAssessmentId?: string | null
  practicalAssessmentId?: string | null
  /** Migration 0022 — `null` desvincula; ausente deixa como está (no PATCH). */
  customerId?: string | null
  jobProfileId?: string | null
  /**
   * Migration 0024 — NÃO anulável, ao contrário dos dois de cima: ausente pede
   * ao servidor que resolva o padrão, e desvincular não é um gesto que exista.
   */
  pipelineId?: string
}

export type UpdateOpportunityInput = Partial<CreateOpportunityInput>

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

/** GET /api/opportunities — paginated list, optional status filter. */
export async function listOpportunities(
  params: {
    status?: OpportunityStatus
    page?: number
    pageSize?: number
    /**
     * Termo de busca — o servidor procura no título, no slug e no nome do
     * cliente. Sem ele, filtrar no cliente só alcançava a página já carregada,
     * e a vaga fora dos primeiros 200 era invisível.
     */
    q?: string
    signal?: AbortSignal
  } = {},
): Promise<OpportunitiesListResponse> {
  const url = new URL(`${API_BASE}/opportunities`, window.location.origin)
  if (params.status) url.searchParams.set('status', params.status)
  url.searchParams.set('page', String(params.page ?? 1))
  url.searchParams.set('pageSize', String(params.pageSize ?? 50))
  // Só quando há termo: um `q=` vazio não filtra nada e ainda dava duas chaves
  // de cache do TanStack Query para a mesma resposta.
  if (params.q?.trim()) url.searchParams.set('q', params.q.trim())

  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (params.signal) init.signal = params.signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) throw new Error(`Failed to list opportunities (${res.status})`)
  return (await res.json()) as OpportunitiesListResponse
}

/** GET /api/opportunities/:id. */
export async function getOpportunity(id: string, signal?: AbortSignal): Promise<Opportunity> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}`, init)
  if (!res.ok) throw new Error(`Failed to load opportunity (${res.status})`)
  return (await res.json()) as Opportunity
}

/** POST /api/opportunities. */
export async function createOpportunity(body: CreateOpportunityInput): Promise<Opportunity> {
  const res = await fetch(`${API_BASE}/opportunities`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Failed to create opportunity (${res.status})`)
  return (await res.json()) as Opportunity
}

/** PATCH /api/opportunities/:id. */
export async function updateOpportunity(
  id: string,
  body: UpdateOpportunityInput,
): Promise<Opportunity> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Failed to update opportunity (${res.status})`)
  return (await res.json()) as Opportunity
}

/**
 * DELETE /api/opportunities/:id — ARQUIVA (soft: status → 'archived').
 *
 * A resposta traz `activeApplicationCount`: quantas candidaturas desta vaga
 * continuam em andamento no instante em que se arquivou. O servidor NÃO recusa
 * quando esse número é maior que zero — arquivar não encerra ninguém — mas
 * também não se cala: é com esta contagem que a interface diz a consequência.
 *
 * A VOLTA é `updateOpportunity(id, { status: 'closed' })`. Aqui o estado é um
 * enum de três valores e não uma data, então arquivar SOBRESCREVE o estado
 * anterior — desarquivar não sabe se a vaga estava aberta ou fechada, e por isso
 * devolve-a como Fechada, que é a afirmação mais fraca das duas.
 */
export async function archiveOpportunity(id: string): Promise<Opportunity> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`Failed to archive opportunity (${res.status})`)
  return (await res.json()) as Opportunity
}

// ------------------------------------------------------------
// Document sub-resource (Story 12.1)
// ------------------------------------------------------------

/**
 * POST /api/opportunities/:id/document — upload (attach/replace) the document.
 * multipart/form-data; the browser sets the boundary, so do NOT set
 * Content-Type manually. Returns the updated opportunity (with `document_*`).
 */
export async function uploadOpportunityDocument(id: string, file: File): Promise<Opportunity> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/document`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: form,
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Falha ao enviar o documento (${res.status})`, [
        'title',
        'detail',
      ]),
    )
  }
  return (await res.json()) as Opportunity
}

/**
 * GET /api/opportunities/:id/document — presigned download URL + metadata.
 * Throws when no document is attached (404).
 */
export async function getOpportunityDocumentUrl(
  id: string,
  signal?: AbortSignal,
): Promise<OpportunityDocument> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/document`, init)
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Falha ao obter o documento (${res.status})`, [
        'title',
        'detail',
      ]),
    )
  }
  return (await res.json()) as OpportunityDocument
}

/** DELETE /api/opportunities/:id/document — detach the document. */
export async function deleteOpportunityDocument(id: string): Promise<Opportunity> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/document`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Falha ao remover o documento (${res.status})`, [
        'title',
        'detail',
      ]),
    )
  }
  return (await res.json()) as Opportunity
}

// ---- Catálogo público da VAGA (migration 0041, Story 16.1) ----
//
// O TERCEIRO link público do produto, a seguir ao da avaliação e ao da
// candidatura. Aqueles servem o RELATÓRIO de uma pessoa; este serve a VITRINE
// de uma vaga — os mesmos perfis anónimos que o cliente vê no portal.
//
// A consequência prática, e o que o cartão da UI diz por palavras: o conjunto
// mostrado MUDA sozinho. Apresentar mais alguém àquela vaga acrescenta-o ao
// catálogo sem trocar o link; retirar alguém tira-o.
//
// ⚠️ O `url` que volta destas três aponta para o SPA (`<origem>/catalog/<token>`)
// e não para o worker, ao contrário dos links de relatório. Quem decide isso é o
// servidor — esta camada só o transporta.

/** GET /api/opportunities/:id/share — estado atual do catálogo público. */
export async function getOpportunityShareState(
  id: string,
  signal?: AbortSignal,
): Promise<ShareState> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/share`, init)
  if (!res.ok) throw new Error(`Failed to load catalog share state (${res.status})`)
  return (await res.json()) as ShareState
}

/** POST /api/opportunities/:id/share — liga o catálogo e gera um token novo. */
export async function enableOpportunityShare(id: string): Promise<ShareState> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`Failed to enable catalog share (${res.status})`)
  return (await res.json()) as ShareState
}

/** DELETE /api/opportunities/:id/share — desliga o catálogo (guarda o histórico). */
export async function disableOpportunityShare(id: string): Promise<ShareState> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/share`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`Failed to disable catalog share (${res.status})`)
  return (await res.json()) as ShareState
}

// ------------------------------------------------------------------
// Os e-mails da vaga (migration 0050) — quem recebe as atualizações dela.
// Só o CADASTRO por agora: os envios ainda não existem.
// ------------------------------------------------------------------

/** Um e-mail cadastrado. `email` vem normalizado pelo servidor (minúsculas). */
export interface OpportunityEmail {
  id: string
  email: string
  createdAt: string
}

/** GET /api/opportunities/:id/emails — a lista, pela ordem de cadastro. */
export async function listOpportunityEmails(
  id: string,
  signal?: AbortSignal,
): Promise<OpportunityEmail[]> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/emails`, init)
  if (!res.ok) throw new Error(`Failed to load opportunity emails (${res.status})`)
  return ((await res.json()) as { items: OpportunityEmail[] }).items
}

/**
 * POST /api/opportunities/:id/emails. O erro traz a frase do servidor ("Informe
 * um e-mail válido.", "O e-mail … já está cadastrado nesta vaga.") — é ela que a
 * tela mostra.
 */
export async function addOpportunityEmail(id: string, email: string): Promise<OpportunityEmail> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/emails`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ email }),
  })
  if (!res.ok) {
    throw new Error(await readProblemMessage(res, 'Não foi possível cadastrar o e-mail.'))
  }
  return (await res.json()) as OpportunityEmail
}

/** DELETE /api/opportunities/:id/emails/:emailId. */
export async function removeOpportunityEmail(id: string, emailId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/opportunities/${encodeURIComponent(id)}/emails/${encodeURIComponent(emailId)}`,
    { method: 'DELETE', credentials: 'include', headers: { Accept: 'application/json' } },
  )
  if (!res.ok) {
    throw new Error(await readProblemMessage(res, 'Não foi possível remover o e-mail.'))
  }
}

/**
 * POST /api/opportunities/:id/share/email — envia o link da vitrine por e-mail
 * aos e-mails da vaga, com cópia para quem clicou (Story 23.15). Devolve quem
 * recebeu. O erro traz a frase do servidor: catálogo desligado, vitrine vazia,
 * ou o e-mail que não saiu.
 */
export async function sendOpportunityShareEmail(id: string): Promise<ShareEmailRecipients> {
  const res = await fetch(`${API_BASE}/opportunities/${encodeURIComponent(id)}/share/email`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(await readProblemMessage(res, 'Não foi possível enviar o e-mail.'))
  }
  return (await res.json()) as ShareEmailRecipients
}
